/**
 * Tab Store Provider and Hooks
 *
 * React Router DOM 환경에서 탭 기반 네비게이션을 위한 Zustand store 제공자와 훅들
 * - TabStoreProvider: 탭 상태 관리를 위한 Context Provider
 * - useTabStore: 탭 store에서 상태를 선택적으로 구독하는 훅
 * - useOnClickTab: 탭 클릭 시 네비게이션과 상태 업데이트를 처리하는 훅
 */

import { menuKeyAliases, sidebarMenuItems } from '@/components/layouts/sidebar/menu-items';
import { TabStore, createTabStore } from '@/stores/tab/tab-store';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useStore } from 'zustand';

type TabStoreApi = ReturnType<typeof createTabStore>;

const TabStoreContext = createContext<TabStoreApi | null>(null);

/**
 * Tab Store Context Provider
 *
 * 애플리케이션 전체에서 탭 상태를 공유하기 위한 Context Provider
 * useRef를 사용하여 리렌더링 시에도 store 인스턴스가 유지되도록 보장
 *
 * @param children - Provider로 감쌀 자식 컴포넌트들
 */
export const TabStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const storeRef = useRef<TabStoreApi | null>(null);
  // store 인스턴스가 없을 때만 새로 생성 (싱글톤 패턴)
  storeRef.current ??= createTabStore();

  return (
    <TabStoreContext.Provider value={storeRef.current}>
      <TabSynchronizer />
      {children}
    </TabStoreContext.Provider>
  );
};

/**
 * Tab Store Hook
 *
 * 탭 store에서 필요한 상태만 선택적으로 구독하는 훅
 * selector 패턴을 통해 성능 최적화 (필요한 상태 변경 시에만 리렌더링)
 *
 * @template T - selector 함수의 반환 타입
 * @param selector - store에서 필요한 상태를 선택하는 함수
 * @returns selector 함수가 반환하는 값
 * @throws TabStoreProvider 외부에서 사용 시 에러 발생
 */
export const useTabStore = <T,>(selector: (store: TabStore) => T): T => {
  const tabStoreContext = useContext(TabStoreContext);
  if (!tabStoreContext) {
    throw new Error(`useTabStore must be used within TabStoreProvider`);
  }

  return useStore(tabStoreContext, selector);
};

/** 최초 진입, 브라우저 prev/next 버튼 등 외부에서 pathname이 변경될 때마다 tabStore에 Sync */
function TabSynchronizer() {
  const { pathname } = useLocation();

  const addTab = useTabStore((store) => store.addTab);
  const setCurrentTabKey = useTabStore((store) => store.setCurrentTabKey);

  useEffect(() => {
    // 자체 메뉴가 없는 경로는 소속 메뉴 key로 치환하여 해당 메뉴가 활성화되도록 한다.
    const menuKey = menuKeyAliases[pathname] ?? pathname;

    const isValidPathname = sidebarMenuItems.some((parent) =>
      parent.children.some((child) => 'key' in child && child.key === menuKey),
    );

    if (isValidPathname) {
      addTab({ key: menuKey });
      setCurrentTabKey(menuKey);
    }
  }, [pathname, addTab, setCurrentTabKey]);

  return null;
}

/**
 * Tab Click Handler Hook
 *
 * 탭 클릭 시 네비게이션과 탭 상태 업데이트를 처리하는 훅
 * React Router DOM의 useNavigate를 사용하여 프로그래밍 방식 네비게이션 제공
 *
 * 주요 기능:
 * 1. 중복 클릭 방지 (현재 활성 탭과 동일한 경우)
 * 2. 새로운 탭 자동 추가
 * 3. 활성 탭 상태 업데이트
 * 4. 저장된 search parameters와 함께 네비게이션
 *
 * @returns onClickTab - 탭 키를 받아 네비게이션을 수행하는 함수
 *
 * @example
 * const onClickTab = useOnClickTab();
 * onClickTab('/dashboard'); // /dashboard로 네비게이션하고 탭 상태 업데이트
 */
export const useOnClickTab = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // 탭 store에서 필요한 상태와 액션들을 선택적으로 구독
  const getTabStore = useTabStore((store) => store.getTabStore);
  const addTab = useTabStore((store) => store.addTab);
  const setCurrentTabKey = useTabStore((store) => store.setCurrentTabKey);

  // useCallback으로 함수 메모이제이션하여 불필요한 리렌더링 방지
  const onClickTab = useCallback(
    (key: string) => {
      const tabStore = getTabStore();

      // 현재 위치와 동일한 경우 중복 네비게이션 방지.
      // currentTabKey는 alias된 메뉴 key일 수 있으므로 실제 pathname으로 비교한다.
      // (예: space-calendar에서 currentTabKey는 spaces-reservations지만 메뉴 클릭 시 이동해야 함)
      if (key === pathname) {
        return;
      }

      // 기존 탭 검색
      const tab = tabStore.tabs.find((tab) => tab.key === key);

      // 새로운 탭일 경우 탭 목록에 추가
      if (!tab) {
        const newTab = { key };
        addTab(newTab);
      }

      // 클릭한 탭을 활성 탭으로 설정
      setCurrentTabKey(key);

      // 기존 탭의 search parameters 복원하여 URL 구성
      const urlSearchParams = new URLSearchParams(tab?.search ?? '');
      void navigate(`${key}${urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : ''}`);
    },
    [getTabStore, addTab, setCurrentTabKey, navigate, pathname],
  );

  return onClickTab;
};
